import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import UpgradeRequiredDialog from './popup/upgrade-required-dialog';
import { useUser } from '../providers/user-provider';
import { useFeatures } from '../providers/features-provider';
import { usePermissions } from '../providers/permissions-provider';
import CompanyAdminsList from './company-admins-list';
import { PLAN_REQUIRED_EVENT } from '../util/plan-required';

// Fired (util/plan-required.ts) whenever the server answers 403 with action
// 'upgrade_required' or 'addon_required' (subscription-guard-service.js - a
// feature or count limit the company's plan doesn't cover). Handled once here,
// app-wide, so every gated action - wherever it's triggered from - opens the
// same prompt instead of each page wiring its own.
export { PLAN_REQUIRED_EVENT };

type PlanRequiredDetail = {
    action: 'upgrade_required' | 'addon_required';
    error?: string;
    toTierId?: string;
    qualifyingTierIds?: string[];
    addonKey?: string;
};

const PlanRequiredGate: React.FC = () => {
    const { user } = useUser();
    const { reload: reloadFeatures } = useFeatures();
    const { isAdmin } = usePermissions();
    const navigate = useNavigate();
    const [detail, setDetail] = React.useState<PlanRequiredDetail | null>(null);

    React.useEffect(() => {
        const onPlanRequired = (e: Event) => setDetail((e as CustomEvent<PlanRequiredDetail>).detail);
        window.addEventListener(PLAN_REQUIRED_EVENT, onPlanRequired);
        return () => window.removeEventListener(PLAN_REQUIRED_EVENT, onPlanRequired);
    }, []);

    if (!detail || user?.entityId == null) return null;
    const close = () => setDetail(null);

    // A plan that covers it exists - straight into the upgrade flow.
    if (detail.action === 'upgrade_required' && detail.toTierId) {
        return (
            <UpgradeRequiredDialog
                open
                onClose={close}
                entityId={user.entityId}
                toTierId={detail.toTierId}
                qualifyingTierIds={detail.qualifyingTierIds}
                message={detail.error}
                onUpgraded={() => { reloadFeatures(); }}
            />
        );
    }

    // An add-on on the current plan (or nothing to upgrade to) - point to
    // where it's turned on.
    return (
        <Dialog open onClose={close} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle fontWeight={600}>
                {detail.action === 'addon_required' ? 'Add-on required' : 'Not included in your plan'}
            </DialogTitle>
            <DialogContent>
                <Typography fontSize={14} color="var(--color-text-muted)">
                    {detail.error || "Your plan doesn't include this."}
                </Typography>
                {/* Only an admin can turn add-ons on - show who to ask. */}
                {!isAdmin && user?.role !== 'master' && <CompanyAdminsList entityId={user.entityId} />}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
                <Button onClick={close} sx={{ textTransform: 'none', color: 'var(--color-text-muted)' }}>Close</Button>
                <Button
                    variant="contained"
                    onClick={() => { close(); navigate('/settings/company#subscription'); }}
                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, boxShadow: 'none', background: 'var(--color-primary)' }}
                >
                    View subscription
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PlanRequiredGate;
